import axios from "axios";
import {browserSupportsWebAuthn, platformAuthenticatorIsAvailable, startAuthentication, startRegistration} from "@simplewebauthn/browser";
import AxiosService from "./axiosService";
import authHeader from "./authHeader";
import AuthService, {clientInfo} from "./authService";

// Clé Pappus de cet appareil (son identifiant) : la page de connexion lance d'elle-même la connexion biométrique,
// et la demande la nomme (empreinte directe, sans choisir de compte)
const DEVICE_KEY = "pappus.biometric.device";
// Nom de l'utilisateur de cette clé : affiché sur la page de connexion
const DEVICE_USER_KEY = "pappus.biometric.username";

/**
 * Connexion biométrique (passkeys WebAuthn) : Face ID ou Touch ID (Apple), empreinte ou visage (Android),
 * Windows Hello. Le visage ou l'empreinte sont vérifiés par l'appareil ; le serveur ne reçoit qu'une signature.
 * Appels axios directs : jamais servis depuis le cache des requêtes.
 */
class WebauthnService {

  url(target) {
    AxiosService.setEndPoint()
    return `${AxiosService.ENDPOINT}/auth/webauthn${target}`
  }

  async post(target, body = {}, auth = true) {
    try {
      const response = await axios.post(this.url(target), body, auth ? {headers: authHeader()} : {})
      return response.data
    } catch (error) {
      error.message = error.response?.data?.message || error.message
      throw error
    }
  }

  /** L'appareil sait-il vérifier l'utilisateur lui-même (visage, empreinte, Windows Hello) ? */
  async available() {
    try {
      return browserSupportsWebAuthn() && await platformAuthenticatorIsAvailable()
    } catch (e) {
      return false
    }
  }

  /** Nom du moyen de vérification de l'appareil : « Face ID », « l'empreinte », « Windows Hello » */
  label() {
    const ua = navigator.userAgent
    if (/iPhone|iPad|iPod/.test(ua)) return "Face ID"
    if (/Macintosh/.test(ua)) return navigator.maxTouchPoints > 1 ? "Face ID" : "Touch ID"
    if (/Android/.test(ua)) return "l'empreinte"
    if (/Windows/.test(ua)) return "Windows Hello"
    return "la biométrie"
  }

  /** Active la connexion biométrique sur cet appareil (utilisateur connecté) */
  async register() {
    const options = await this.post("/register/options")
    const response = await startRegistration({optionsJSON: options})
    const {credential} = await this.post("/register", {response})
    this.setDevice(response.id, AuthService.getStoreInstance().user?.username)
    return credential
  }

  /** Connexion : l'appareil propose ses clés Pappus, vérifie l'utilisateur, le serveur ouvre la session */
  async login() {
    const {challengeId, options} = await this.post("/login/options", {credentialId: this.device()}, false)
    const response = await startAuthentication({optionsJSON: options})
    let user
    try {
      user = await this.post("/login", {challengeId, response, client: await clientInfo()}, false)
    } catch (error) {
      // Clé retirée du profil : la page de connexion ne la propose plus d'elle-même
      if (error.response?.status === 401) this.setDevice(null)
      throw error
    }
    this.setDevice(response.id, user.username)
    if (user.accessToken) {
      await AuthService.getStoreInstance().login(user)
    }
    return user
  }

  /**
   * La clé de cet appareil est-elle toujours activée sur le serveur ? Clé retirée : oubliée ici.
   * Serveur injoignable : on la garde (true), la connexion biométrique dira si elle échoue.
   */
  async isActive() {
    const credentialId = this.device()
    if (!credentialId) return false
    try {
      const {active} = await this.post("/status", {credentialId}, false)
      if (!active) {
        this.signalRemoved(credentialId)
        this.setDevice(null)
      }
      return active
    } catch (error) {
      return true
    }
  }

  /*
   * Signal API (Chrome, Edge 132+, Safari 26) : retirer une clé du serveur ne l'efface pas de l'appareil (Windows Hello,
   * trousseau, gestionnaire Google), qui refuserait ensuite d'en créer une nouvelle (InvalidStateError). On lui
   * signale les clés qui ne valent plus ; ailleurs, rien (le message d'erreur explique comment la supprimer).
   */

  /** Clé retirée de Pappus : l'appareil qui la garde peut l'effacer */
  signalRemoved(credentialId) {
    if (!credentialId || typeof PublicKeyCredential === "undefined" || !PublicKeyCredential.signalUnknownCredential) return
    PublicKeyCredential.signalUnknownCredential({rpId: location.hostname, credentialId}).catch(() => {})
  }

  /** Clés Pappus encore valables de l'utilisateur connecté : l'appareil efface les autres (retirées ailleurs) */
  signalAccepted(credentialIds) {
    const userId = AuthService.getStoreInstance().user?.id
    if (!userId || typeof PublicKeyCredential === "undefined" || !PublicKeyCredential.signalAllAcceptedCredentials) return
    // Même identifiant d'utilisateur qu'à l'activation (backend : TextEncoder(String(user.id))), en base64url
    const userHandle = btoa(String(userId)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
    PublicKeyCredential.signalAllAcceptedCredentials({rpId: location.hostname, userId: userHandle, allAcceptedCredentialIds: credentialIds})
      .catch(() => {})
  }

  /** Appareils activés de l'utilisateur connecté : [{id, deviceName, createdAt, lastUsedAt}] */
  async credentials() {
    AxiosService.setEndPoint()
    const response = await axios.get(this.url("/credentials"), {headers: authHeader()})
    return response.data.credentials
  }

  async remove(id) {
    AxiosService.setEndPoint()
    await axios.delete(this.url(`/credentials/${id}`), {headers: authHeader()})
  }

  /** Identifiant de la clé Pappus de cet appareil, ou null (« on » : connue d'une version précédente, sans identifiant) */
  device() {
    try {
      return localStorage.getItem(DEVICE_KEY)
    } catch (e) {
      return null
    }
  }

  /** Nom de l'utilisateur de la clé de cet appareil */
  deviceUser() {
    try {
      return localStorage.getItem(DEVICE_USER_KEY)
    } catch (e) {
      return null
    }
  }

  setDevice(credentialId, username = null) {
    try {
      if (credentialId) localStorage.setItem(DEVICE_KEY, credentialId)
      else localStorage.removeItem(DEVICE_KEY)
      if (credentialId && username) localStorage.setItem(DEVICE_USER_KEY, username)
      else if (!credentialId) localStorage.removeItem(DEVICE_USER_KEY)
    } catch (e) {
      // Stockage indisponible : le bouton reste là
    }
  }

  /** Annulation par l'utilisateur (fenêtre de l'appareil fermée) : pas un échec à afficher */
  cancelled(error) {
    return error && (error.name === "NotAllowedError" || error.name === "AbortError")
  }

  /**
   * Message d'un échec, compréhensible : l'appareil ne dit pas pourquoi il refuse (annulation, délai dépassé,
   * biométrie non configurée…), on donne les causes possibles ; le détail technique suit entre parenthèses
   */
  explain(error, fallback) {
    if (!error) return fallback
    // Clé d'une activation précédente restée sur l'appareil (retirée de Pappus, mais pas de l'appareil)
    if (error.name === "InvalidStateError") {
      return "Une ancienne clé Pappus est restée sur cet appareil. Supprime-la dans ses réglages (Windows : Paramètres › Comptes"
        + " › Clés d'accès ; iPhone : Réglages › Mots de passe ; Android : Gestionnaire de mots de passe Google), puis réessaie."
    }
    if (error.name === "NotAllowedError" || error.name === "AbortError") {
      return "L'appareil n'a pas validé : opération annulée, délai dépassé, ou " + this.label() + " non configuré sur l'appareil."
    }
    if (error.name === "SecurityError") return "Connexion biométrique impossible sur cette adresse (" + location.hostname + ")."
    return (error.message || fallback) + (error.name && error.name !== "Error" ? " (" + error.name + ")" : "")
  }
}

export default new WebauthnService();
